import { ApprovalsDomainConfigurationOperationsEnum } from 'generated/resources/datatable.graphql'
import { computed, makeObservable } from 'mobx'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'
import type { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'

import type { Category } from 'modules/approvals/lib/types'

export interface SelectorOption {
  label: string
  value: string
  parent_id: string | null
}

export interface DomainConfigurationRule {
  identifier: string
  parent_rule_identifier: string | null
  label: string
  input_type: string
  mandatory: boolean
  input_width: 'full' | 'half'
  selector_options: [SelectorOption]
  operation: ApprovalsDomainConfigurationOperationsEnum
}

export interface IApprovalGroup {
  id: number
  name: string
  default: boolean
  description: string
  category: Category
  auto_approved: boolean
  domain_configuration_rules: [DomainConfigurationRule]
  domain_configuration_rules_status: string
}

class ApprovalGroup extends Model<IApprovalGroup> {
  static urlRoot() {
    return '/approvals/approval_groups'
  }

  constructor(attr: IApprovalGroup) {
    super(attr)
    makeObservable(this, {
      name: computed,
      default: computed,
    })
  }

  static RULES_CONFIGURATION_STATUS = {
    enabled: 'enabled',
    disabled: 'disabled',
    upgradePlan: 'upgrade_plan',
  }

  get name(): string {
    return this.attributes.name
  }

  get id(): number {
    return this.attributes.id
  }

  get category(): Category {
    return this.attributes.category
  }

  get description(): string {
    return this.attributes.description
  }

  get autoApproved(): boolean {
    return this.attributes.auto_approved
  }

  get default(): boolean {
    return this.attributes.default
  }

  get domainConfigurationRules(): [DomainConfigurationRule] {
    return this.attributes.domain_configuration_rules
  }

  get domainConfigurationRulesStatus(): string {
    return this.attributes.domain_configuration_rules_status
  }

  get domainConfigurationRulesEnabled(): boolean {
    return (
      this.attributes.domain_configuration_rules_status ===
      ApprovalGroup.RULES_CONFIGURATION_STATUS.enabled
    )
  }

  get domainConfigurationRulesDisabled(): boolean {
    return (
      this.attributes.domain_configuration_rules_status ===
      ApprovalGroup.RULES_CONFIGURATION_STATUS.disabled
    )
  }

  get domainConfigurationRulesUpgradePlane(): boolean {
    return (
      this.attributes.domain_configuration_rules_status ===
      ApprovalGroup.RULES_CONFIGURATION_STATUS.upgradePlan
    )
  }

  get resourceType(): string {
    const RESOURCE_TYPES = {
      timeoff: i18n.t('settings.approvals.resource_type.timeoff'),
      expenses: i18n.t('settings.approvals.resource_type.expenses'),
      time_tracking: i18n.t('settings.approvals.resource_type.time_tracking'),
      ats: i18n.t('settings.approvals.resource_type.ats'),
      contracts: 'contracts',
      software: i18n.t('settings.approvals.resource_type.software'),
      pruchases: i18n.t('settings.approvals.resource_type.purchases'),
      trainings: i18n.t('settings.approvals.resource_type.trainings'),
    }

    return RESOURCE_TYPES[this.category as keyof typeof RESOURCE_TYPES]
  }

  backUrl(navigation: Navigation<NavigationPaths>): string {
    const BACKROUTES = {
      timeoff: navigation.settings.timeoffPolicies.fullPath(),
      expenses: navigation.settings.expenses.fullPath(),
      time_tracking: navigation.settings.time_tracking.fullPath(),
      ats: navigation.settings.ats.fullPath(),
      contracts: navigation.settings.contracts_settings.fullPath(),
      software: navigation.settings.software.fullPath(),
      purchases: navigation.settings.procurement.fullPath(),
      trainings: navigation.settings.trainings.fullPath(),
    }

    return BACKROUTES[this.category as keyof typeof BACKROUTES]
  }
}

export default ApprovalGroup
